const SCALE_FACTOR = 12
const STROKE_COLOR = '#097995'
const REFLINE_COLOR = '#F2F5F9'
const INTER_LINE_COLOR = 'rgba(9, 121, 149, 0.1)'
const CIRCLE_STROKE = '#1B222E'
const CIRCLE_FILL = '#F2F5F9'

let GRAPH_TOP: number
let GRAPH_LEFT: number
let GRAPH_BOTTOM: number
let GRAPH_RIGHT: number
let GRAPH_MID: number
let dpr = 1

export interface BikeElevationSection {
  elevation_color: string
  elevation_data: number[]
}
interface LineGraphData {
  ceil: number
  ctx: CanvasRenderingContext2D
  dataLength: number
  dataList: number[]
  dataSections: BikeElevationSection[]
  floor: number
  pointsPerPixel: number
}

export function createLineGraph(
  canvas: HTMLCanvasElement,
  dataSections: BikeElevationSection[]
): void {
  const ctx = canvas.getContext('2d')
  if (ctx === null || dataSections.length === 0) {
    return
  }
  setDimensions(canvas, ctx)

  const dataList: number[] = []
  dataSections.forEach((d) => {
    dataList.push(...d.elevation_data)
  })
  const pointsPerPixel = dataList.length / canvas.clientWidth
  const optimizedSections = dataSections.map((section) => {
    return {
      elevation_color: section.elevation_color,
      elevation_data: spaceOptimisedPoints(section.elevation_data, pointsPerPixel),
    }
  })
  const optimizedList: number[] = []
  optimizedSections.forEach((d) => {
    optimizedList.push(...d.elevation_data)
  })

  const lineGraphData: LineGraphData = {
    ceil: 0,
    ctx,
    dataLength: optimizedList.length,
    dataList: optimizedList,
    dataSections: optimizedSections,
    floor: Infinity,
    pointsPerPixel,
  }

  lineGraphData.ceil = Math.max(...lineGraphData.dataList)
  lineGraphData.floor = Math.min(...lineGraphData.dataList)

  const buffer =
    lineGraphData.ceil === lineGraphData.floor
      ? 5
      : Math.ceil(0.2 * (lineGraphData.ceil - lineGraphData.floor))
  lineGraphData.ceil = lineGraphData.ceil + buffer
  lineGraphData.floor = lineGraphData.floor - buffer

  drawReferenceLines(lineGraphData) // Draw Horizontal Ref lines
  drawFillPolygon(lineGraphData) // Fill polygon below the line
  drawLinePolygon(lineGraphData) // Draw Actual line

  // Draw EndPoint circles
  drawCircle(lineGraphData, 0, 6.5 * dpr)
  drawCircle(lineGraphData, lineGraphData.dataLength - 1, 6.5 * dpr)
  drawCircle(lineGraphData, lineGraphData.dataLength - 1, 2.75 * dpr, STROKE_COLOR, STROKE_COLOR)
}

function setDimensions(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
  const originalHeight = canvas.height
  const originalWidth = canvas.width

  const ratio = canvas.width / canvas.height
  const clientRatio = canvas.clientWidth / canvas.clientHeight
  let targetWidth = 0
  let targetHeight = 0

  if (ratio > clientRatio) {
    targetWidth = canvas.clientWidth
    targetHeight = targetWidth / ratio
  } else {
    targetHeight = canvas.clientHeight
    targetWidth = targetHeight * ratio
  }

  dpr = (window.devicePixelRatio || 1) * SCALE_FACTOR
  canvas.width = targetWidth * dpr
  canvas.height = targetHeight * dpr

  const min = Math.min(canvas.clientWidth / originalWidth, canvas.clientHeight / originalHeight)
  ctx.scale((min * dpr) / SCALE_FACTOR, (min * dpr) / SCALE_FACTOR)

  GRAPH_TOP = Number(dpr)
  GRAPH_LEFT = 9 * dpr
  GRAPH_RIGHT = canvas.width - GRAPH_LEFT
  GRAPH_BOTTOM = canvas.height - GRAPH_TOP
  GRAPH_MID = (GRAPH_BOTTOM + GRAPH_TOP) / 2
}

const getX = ({ dataLength }: LineGraphData, index: number): number =>
  ((GRAPH_RIGHT - GRAPH_LEFT) * index) / (dataLength - 1) + GRAPH_LEFT

const getY = ({ ceil, floor, dataList }: LineGraphData, index: number): number =>
  GRAPH_BOTTOM - ((dataList[index] - floor) / (ceil - floor)) * (GRAPH_BOTTOM - GRAPH_TOP)

function drawReferenceLines({ ctx }: LineGraphData): void {
  ctx.beginPath()
  ctx.lineWidth = 1.5 * dpr
  ctx.strokeStyle = REFLINE_COLOR
  ctx.moveTo(GRAPH_LEFT, GRAPH_TOP)
  ctx.lineTo(GRAPH_RIGHT, GRAPH_TOP)
  ctx.moveTo(GRAPH_LEFT, GRAPH_MID)
  ctx.lineTo(GRAPH_RIGHT, GRAPH_MID)
  ctx.moveTo(GRAPH_LEFT, GRAPH_BOTTOM)
  ctx.lineTo(GRAPH_RIGHT, GRAPH_BOTTOM)
  ctx.stroke()
}

function drawFillPolygon(lineGraphData: LineGraphData): void {
  const { ctx } = lineGraphData
  ctx.beginPath()
  ctx.lineJoin = 'round'

  ctx.beginPath()
  ctx.moveTo(GRAPH_LEFT, GRAPH_BOTTOM)

  ctx.strokeStyle = 'transparent'
  ctx.fillStyle = INTER_LINE_COLOR
  ctx.lineTo(getX(lineGraphData, 0), getY(lineGraphData, 0))

  lineGraphData.dataList.forEach((_, index) => {
    ctx.lineTo(getX(lineGraphData, index), getY(lineGraphData, index))
  })

  ctx.lineTo(GRAPH_RIGHT, GRAPH_BOTTOM)

  ctx.stroke()
  ctx.fill()
}

function drawLinePolygon(lineGraphData: LineGraphData): void {
  const { ctx, dataSections } = lineGraphData

  ctx.fillStyle = 'transparent'
  let index = 0
  let prevX = getX(lineGraphData, index)
  let prevY = getY(lineGraphData, index)

  dataSections.forEach(({ elevation_color, elevation_data }) => {
    // Draw intermediate circle
    if (index > 0) {
      // Draw intermediate vertical reference line
      ctx.beginPath()
      ctx.moveTo(prevX, prevY)
      ctx.lineWidth = 1.5 * dpr
      ctx.strokeStyle = INTER_LINE_COLOR
      ctx.lineTo(prevX, GRAPH_BOTTOM)
      ctx.stroke()

      // Draw intermediate circles
      drawCircle(lineGraphData, index - 1, 2.5 * dpr, elevation_color, elevation_color)
    }

    // Draw actual path
    ctx.beginPath()
    ctx.moveTo(prevX, prevY)
    elevation_data.forEach(() => {
      ctx.lineWidth = 2.5 * dpr
      ctx.strokeStyle = elevation_color
      prevX = getX(lineGraphData, index)
      prevY = getY(lineGraphData, index)

      ctx.lineTo(prevX, prevY)
      index++
    })
    ctx.stroke()
  })
}

function drawCircle(
  lineGraphData: LineGraphData,
  index: number,
  radius: number,
  strokeColor = CIRCLE_STROKE,
  fillColor = CIRCLE_FILL
): void {
  const { ctx } = lineGraphData
  ctx.lineWidth = 2 * dpr
  ctx.beginPath()
  ctx.strokeStyle = strokeColor
  ctx.fillStyle = fillColor
  ctx.arc(getX(lineGraphData, index), getY(lineGraphData, index), radius, 0, 2 * Math.PI)
  ctx.fill()
  ctx.stroke()
}

function spaceOptimisedPoints(dataList: number[], pointsPerPixel: number): number[] {
  const smoothingFactor = 8
  const optimizedPoints = []
  if (pointsPerPixel <= 1) {
    return dataList
  }
  let subList
  let end: number
  const smoothPointPerPixel = pointsPerPixel * smoothingFactor
  for (let i = 0; i < dataList.length; i += smoothPointPerPixel) {
    end = i + smoothPointPerPixel
    subList = dataList.slice(Math.ceil(i), end > dataList.length ? dataList.length : Math.ceil(end))
    if (subList.length === 0) {
      break
    }

    const sumVal = subList.reduce((total, point) => total + point, 0)

    optimizedPoints.push(sumVal / subList.length)
  }
  return optimizedPoints
}
